import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';
import { useWorker } from '@koale/useworker';
import { saveAs } from 'file-saver';
import _ from 'lodash';

import { useTheme } from '../../ThemeContext';

import Helmet from '../../components/Helmet';
import LoadingData from '../../components/LoadingData';
import Header from '../../components/Header';
import HeaderLabels from '../../components/HeaderLabels';
import Sider from '../../components/Sider';
import PhotoDocument from '../../components/PhotoDocument';
import PhotoDocumentDetails from '../../components/PhotoDocumentDetails';
import PhotoDocumentRelabel from '../../components/PhotoDocumentRelabel';
import PhotoDocumentReject from '../../components/PhotoDocumentReject';
import PhotoDocumentDelete from '../../components/PhotoDocumentDelete';
import PhotoDocumentsRelabel from '../../components/PhotoDocumentsRelabel';
import PhotoDocumentsReject from '../../components/PhotoDocumentsReject';
import PhotoDocumentsDelete from '../../components/PhotoDocumentsDelete';
import Filter from '../../components/Filter';
import BatchOperations from '../../components/BatchOperations';

import { getCountPhotos } from './helpers/getCountPhotos';

import {
  Button,
  Section,
  Wrapper,
  Content,
  Panel,
  PanelInner,
  Information,
  Text,
  Center,
  Box,
  Navigation,
  NavigationColumn,
  SelectionHolder,
  Selection,
  SelectionInput,
  SelectionCheckmark,
} from './ViewerPage.style';

const {
  Parser,
  transforms: { flatten },
} = require('json2csv');

const ViewerPage = () => {
  const panelRef = useRef(null);
  const panelInnerRef = useRef(null);
  const intl = useIntl();
  const theme = useTheme();
  const { profile } = useSelector((state) => state.firebase);
  const filterStore = useSelector((state) => state.filterStore);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const [defaultPhotoDocumentsLimit] = useState(50);
  const [currentPhotoDocumentsLimit, setCurrentPhotoDocumentsLimit] = useState(
    defaultPhotoDocumentsLimit
  );
  const statistics = useSelector((state) => state.firestore.data['statistics']);

  const photoDocuments = useSelector(
    (state) => state.firestore.ordered['photos']
  );

  const [selectedPhotoDocumentId, setSelectedPhotoDocumentId] = useState(
    undefined
  );
  const selectedPhotoDocument =
    selectedPhotoDocumentId &&
    photoDocuments &&
    photoDocuments.find(
      (photoDocument) => photoDocument.id === selectedPhotoDocumentId
    );

  const [
    availableChangeLeftPhotoDocument,
    setAvailableChangeLeftPhotoDocument,
  ] = useState(false);
  const [
    availableChangeRightPhotoDocument,
    setAvailableChangeRightPhotoDocument,
  ] = useState(false);
  const [visiblePhotoDocumentModal, setVisiblePhotoDocumentModal] = useState(
    false
  );
  const [
    visiblePhotoDocumentRelabelModal,
    setVisiblePhotoDocumentRelabelModal,
  ] = useState(false);
  const [
    visiblePhotoDocumentRejectModal,
    setVisiblePhotoDocumentRejectModal,
  ] = useState(false);
  const [
    visiblePhotoDocumentDeleteModal,
    setVisiblePhotoDocumentDeleteModal,
  ] = useState(false);
  const [
    visiblePhotoDocumentsRelabelModal,
    setVisiblePhotoDocumentsRelabelModal,
  ] = useState(false);
  const [
    visiblePhotoDocumentsRejectModal,
    setVisiblePhotoDocumentsRejectModal,
  ] = useState(false);
  const [
    visiblePhotoDocumentsDeleteModal,
    setVisiblePhotoDocumentsDeleteModal,
  ] = useState(false);
  const [
    photoDocumentRelabelProcess,
    setPhotoDocumentRelabelProcess,
  ] = useState(false);
  const [photoDocumentRejectProcess, setPhotoDocumentRejectProcess] = useState(
    false
  );
  const [photoDocumentDeleteProcess, setPhotoDocumentDeleteProcess] = useState(
    false
  );

  const [selectedPhotoDocuments, setSelectedPhotoDocuments] = useState([]);
  const [countPhotosWorker, { status: workerStatus }] = useWorker(
    getCountPhotos
  );
  const [countPhotos, setCountPhotos] = useState(undefined);

  const fn = (t) => {
    handleScrollEventListener(t);
  };
  const debouncedScroll = _.debounce(fn, 100);

  const handleScrollEventListener = (target) => {
    const node = target;
    const isScrollToBottomEdge =
      node.scrollHeight - node.scrollTop <= node.clientHeight;
    if (
      isScrollToBottomEdge &&
      photoDocuments &&
      isLoaded(photoDocuments) &&
      countPhotos &&
      countPhotos.criteria &&
      countPhotos.criteria.length > currentPhotoDocumentsLimit
    ) {
      setShowScrollLoader(true);
      handleLoadPhotoDocuments();
    }
  };

  const handleLoadPhotoDocuments = () =>
    setCurrentPhotoDocumentsLimit(
      currentPhotoDocumentsLimit + defaultPhotoDocumentsLimit
    );

  const handleOpenViewPhotoDocumentDetails = (id) => {
    setSelectedPhotoDocumentId(id);
    setVisiblePhotoDocumentModal(true);
  };

  const handleCloseViewPhotoDocumentDetails = () => {
    setVisiblePhotoDocumentModal(false);
    setSelectedPhotoDocumentId(undefined);
    setAvailableChangeLeftPhotoDocument(false);
    setAvailableChangeRightPhotoDocument(false);
  };

  const handleAddSelectPhotoDocument = (photoDocument) =>
    setSelectedPhotoDocuments([...selectedPhotoDocuments, photoDocument]);

  const handleAddSelectPhotoDocuments = () =>
    setSelectedPhotoDocuments([...photoDocuments]);

  const handleRemoveSelectPhotoDocument = (photoDocument) => {
    const { id } = photoDocument;
    setSelectedPhotoDocuments([
      ...selectedPhotoDocuments.filter(
        (photoDocument) => String(photoDocument.id) !== String(id)
      ),
    ]);
  };

  const handleRemoveSelectPhotoDocuments = () => setSelectedPhotoDocuments([]);

  const handleCheckSelectedPhotoDocuments = () => {
    const ids =
      (photoDocuments &&
        photoDocuments.length > 0 &&
        photoDocuments.map(({ id }) => String(id))) ||
      [];
    setSelectedPhotoDocuments([
      ...selectedPhotoDocuments.filter((photoDocument) =>
        ids.includes(String(photoDocument.id))
      ),
    ]);
  };

  const handleAction = (action = {}) => {
    if (action.type === 'RELABEL') {
      setVisiblePhotoDocumentRelabelModal(true);
    } else if (action.type === 'REJECT') {
      setVisiblePhotoDocumentRejectModal(true);
    } else if (action.type === 'DELETE') {
      setVisiblePhotoDocumentDeleteModal(true);
    } else if (action.type === 'BATCH_RELABEL') {
      setVisiblePhotoDocumentsRelabelModal(true);
    } else if (action.type === 'BATCH_REJECT') {
      setVisiblePhotoDocumentsRejectModal(true);
    } else if (action.type === 'BATCH_DELETE') {
      setVisiblePhotoDocumentsDeleteModal(true);
    } else if (action.type === 'BATCH_DOWNLOAD_CSV') {
      exportToCSV();
    } else if (action.type === 'BATCH_DOWNLOAD_JSON') {
      exportToJSON();
    } else {
      setVisiblePhotoDocumentRelabelModal(false);
      setVisiblePhotoDocumentRejectModal(false);
      setVisiblePhotoDocumentDeleteModal(false);
      setVisiblePhotoDocumentsRelabelModal(false);
      setVisiblePhotoDocumentsRejectModal(false);
      setVisiblePhotoDocumentsDeleteModal(false);
    }
  };

  const handleChangePhotoDocument = (type) => {
    if (photoDocuments && photoDocuments.length > 0) {
      const photoDocumentIndex = photoDocuments.findIndex(
        (photo) => String(photo.id) === String(selectedPhotoDocumentId)
      );
      if (photoDocumentIndex !== -1) {
        if (type === 'prev' && photoDocumentIndex > 0) {
          let newSelectedPhoto = photoDocuments[photoDocumentIndex - 1];
          setSelectedPhotoDocumentId(newSelectedPhoto.id);
        } else if (
          type === 'next' &&
          photoDocumentIndex < photoDocuments.length - 1
        ) {
          let newSelectedPhoto = photoDocuments[photoDocumentIndex + 1];
          setSelectedPhotoDocumentId(newSelectedPhoto.id);
        }
      }
    }
  };

  const handleRelabelProcess = (status) =>
    setPhotoDocumentRelabelProcess(status);

  const handleRejectProcess = (status) => setPhotoDocumentRejectProcess(status);

  const handleDeleteProcess = (status) => setPhotoDocumentDeleteProcess(status);

  const exportToCSV = () => {
    const fileName = 'photo_documents';
    const fileExtension = '.csv';
    const transforms = [flatten({ objects: true, arrays: true })];
    const parser = new Parser({ transforms });
    const csv = parser.parse(selectedPhotoDocuments);
    const blob = new Blob([csv], { type: 'text/csv' });
    saveAs(blob, fileName + fileExtension);
  };

  const exportToJSON = () => {
    const fileName = 'photo_documents';
    const fileExtension = '.json';
    const json = JSON.stringify(selectedPhotoDocuments);
    const blob = new Blob([json], { type: 'text/json' });
    saveAs(blob, fileName + fileExtension);
  };

  const firestoreConnect = [];

  const [showScrollLoader, setShowScrollLoader] = useState(false);

  if (isProfileCompany) {
    const dataSourceTags =
      filterStore.dataSource !== undefined && filterStore.tags !== undefined
        ? (() => {
            const tags = Object.entries(filterStore.tags).reduce(
              (currentState, [category, tags]) => {
                if (tags) {
                  return [
                    ...currentState,
                    ...tags.split(',').map((tag) => `${category}.${tag}`),
                  ];
                }
                return currentState;
              },
              []
            );

            if (tags && tags.length > 0)
              return [
                `${filterStore.recognition || 'vinne_annotation'}.photo.tags`,
                'array-contains-any',
                tags.slice(0, 10),
              ];
            return undefined;
          })()
        : undefined;

    const photosFilters = [
      filterStore.origin !== undefined && [
        'origin_id',
        '==',
        filterStore.origin,
      ],
      filterStore.startDate !== undefined && [
        'creation_date',
        '>=',
        filterStore.startDate,
      ],
      filterStore.endDate !== undefined && [
        'creation_date',
        '<=',
        filterStore.endDate,
      ],
      filterStore.annotated !== undefined && [
        'vinne_annotation.annotated',
        '==',
        filterStore.annotated,
      ],
      filterStore.verified !== undefined && [
        'vinne_verification.verified',
        '==',
        filterStore.verified,
      ],
      filterStore.rejected !== undefined && [
        'rejected',
        '==',
        filterStore.rejected,
      ],
      filterStore.reserved !== undefined && [
        'reserved',
        '==',
        filterStore.reserved,
      ],
      filterStore.user_id !== undefined && [
        'vinne_annotation.user_id',
        '==',
        filterStore.user_id,
      ],
      filterStore.dataSource !== undefined && [
        'data_source_id',
        '==',
        filterStore.dataSource,
      ],
      dataSourceTags,
    ].filter((criteria) => criteria);

    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'photos',
          orderBy: [['creation_date', 'desc']],
          where:
            Object.values(filterStore).filter(
              (criteria) => criteria !== undefined
            ).length > 0
              ? (photosFilters && photosFilters.length > 0 && photosFilters) ||
                undefined
              : undefined,
          limit: currentPhotoDocumentsLimit,
        },
      ],
      storeAs: `photos`,
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'statistics',
          doc: 'overall',
        },
      ],
      storeAs: 'statistics',
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    setShowScrollLoader(false);
    handleCheckSelectedPhotoDocuments();
    // eslint-disable-next-line
  }, [photoDocuments && photoDocuments.length]);

  useEffect(() => {
    if (photoDocuments && photoDocuments.length > 0) {
      const photoDocumentIndex = photoDocuments.findIndex(
        (photo) => String(photo.id) === String(selectedPhotoDocumentId)
      );
      if (photoDocumentIndex !== -1) {
        setAvailableChangeLeftPhotoDocument(
          photoDocumentIndex > 0 ? true : false
        );
        setAvailableChangeRightPhotoDocument(
          photoDocumentIndex < photoDocuments.length - 1 ? true : false
        );
      }
    }
    return () => {
      if (availableChangeLeftPhotoDocument)
        setAvailableChangeLeftPhotoDocument(false);
      if (availableChangeRightPhotoDocument)
        setAvailableChangeRightPhotoDocument(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhotoDocumentId]);

  useEffect(() => {
    if (
      panelRef &&
      panelRef.current &&
      panelInnerRef &&
      panelInnerRef.current
    ) {
      const panelBounds = panelRef.current.getBoundingClientRect();
      const panelInnerBounds = panelInnerRef.current.getBoundingClientRect();
      if (
        panelInnerBounds.height <= panelBounds.height &&
        photoDocuments &&
        isLoaded(photoDocuments) &&
        countPhotos &&
        countPhotos.criteria &&
        countPhotos.criteria.length > currentPhotoDocumentsLimit
      )
        handleLoadPhotoDocuments();
    }

    // eslint-disable-next-line
  }, [
    // eslint-disable-next-line
    photoDocuments && photoDocuments.length,
    // eslint-disable-next-line
    countPhotos && countPhotos.criteria && countPhotos.criteria.length,
  ]);

  useEffect(() => {
    setCurrentPhotoDocumentsLimit(defaultPhotoDocumentsLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStore]);

  useEffect(() => {
    if (statistics) {
      (async () => {
        if (workerStatus !== 'RUNNING') {
          const result = await countPhotosWorker(statistics, filterStore);
          setCountPhotos(result);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statistics, filterStore]);

  const renderCountPhotos = () => {
    return (
      <HeaderLabels
        labels={[
          {
            title: intl.formatMessage({
              id: 'VIEWER.VIEWER_COUNT_PHOTOS',
            }),
            value: countPhotos.criteria.length,
            column: 'LEFT',
          },
          {
            title: intl.formatMessage({
              id: 'VIEWER.VIEWER_COUNT_TAGGED',
            }),
            value: countPhotos.tagged.length,
            column: 'RIGHT',
          },
          {
            title: intl.formatMessage({
              id: 'VIEWER.VIEWER_COUNT_NOT_TAGGED',
            }),
            value: countPhotos.notTagged.length,
            column: 'RIGHT',
          },
          {
            title: intl.formatMessage({
              id: 'VIEWER.VIEWER_TOTAL',
            }),
            value: countPhotos.total.length,
            column: 'RIGHT',
          },
        ]}
      />
    );
  };

  const renderInformation = (information) => {
    return (
      <Center>
        <Information theme={theme.content.information.default}>
          <Text>{information}</Text>
        </Information>
      </Center>
    );
  };
  const renderCurrentSelected = () => {
    return selectedPhotoDocuments && selectedPhotoDocuments.length > 0 ? (
      <Button
        theme={theme.button.normal}
        widthAuto
        noMask
        small
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveSelectPhotoDocuments();
        }}
      >
        {intl.formatMessage({ id: 'VIEWER.DESELECT' }) +
          ' ' +
          selectedPhotoDocuments.length}
      </Button>
    ) : (
      ''
    );
  };
  const renderSelection = () => {
    const areSelectedPhotoDocuments =
      photoDocuments && photoDocuments.length === selectedPhotoDocuments.length
        ? true
        : false;
    return (
      <SelectionHolder
        theme={theme.viewerPage.selectionHolder}
        onClick={(e) => {
          e.stopPropagation();
          if (areSelectedPhotoDocuments) handleRemoveSelectPhotoDocuments();
          else handleAddSelectPhotoDocuments();
        }}
      >
        <Selection
          theme={theme.viewerPage.selection}
          isSelected={areSelectedPhotoDocuments ? true : undefined}
        >
          <SelectionInput
            readOnly
            type='checkbox'
            defaultChecked={areSelectedPhotoDocuments ? true : undefined}
          />
          <SelectionCheckmark
            theme={theme.viewerPage.selectionCheckmark}
            className='material-icons'
            isSelected={areSelectedPhotoDocuments ? true : undefined}
          >
            check
          </SelectionCheckmark>
        </Selection>
        {intl.formatMessage({ id: 'VIEWER.SELECT_ALL' })}
      </SelectionHolder>
    );
  };

  const renderPanelNavigation = () => {
    return (
      <Navigation>
        <NavigationColumn>
          <Box>
            {renderSelection()}
            {renderCurrentSelected()}
          </Box>
        </NavigationColumn>
        <NavigationColumn>
          {selectedPhotoDocuments && selectedPhotoDocuments.length > 0 ? (
            <BatchOperations handleAction={(action) => handleAction(action)} />
          ) : (
            ''
          )}
        </NavigationColumn>
      </Navigation>
    );
  };

  const renderPanelBox = () => {
    const isSelectedPhotoDocument = (id) =>
      selectedPhotoDocuments.find(
        (photoDocument) => String(photoDocument.id) === String(id)
      )
        ? true
        : false;
    return (
      <Box>
        {photoDocuments.map(({ id, ...photoDocument }) => (
          <PhotoDocument
            key={id}
            data={{
              id,
              ...photoDocument,
            }}
            handleClick={(id) => handleOpenViewPhotoDocumentDetails(id)}
            isSelected={isSelectedPhotoDocument(id)}
            handleCheck={(photoDocument) =>
              handleAddSelectPhotoDocument(photoDocument)
            }
            handleUncheck={(photoDocument) =>
              handleRemoveSelectPhotoDocument(photoDocument)
            }
            isSmaller={true}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'PAGE_TITLE.VIEWER' })} />
      <Section>
        <Sider />
        <Wrapper>
          <Header title={intl.formatMessage({ id: 'PAGE_TITLE.VIEWER' })}>
            {isLoaded(photoDocuments) && countPhotos ? renderCountPhotos() : ''}
          </Header>
          <Content>
            <Panel
              ref={panelRef}
              theme={theme.content.panel.default}
              onScroll={(event) => debouncedScroll(event.target)}
            >
              <PanelInner
                ref={panelInnerRef}
                center={
                  !isLoaded(photoDocuments) ||
                  !(photoDocuments && photoDocuments.length > 0)
                    ? true
                    : undefined
                }
              >
                {!isProfileCompany ? (
                  renderInformation(
                    intl.formatMessage({
                      id: `VIEWER.YOU_HAVE_NO_ASSIGNED_COMPANY`,
                    })
                  )
                ) : isLoaded(photoDocuments) ? (
                  !(photoDocuments && photoDocuments.length > 0) ? (
                    renderInformation(
                      intl.formatMessage({
                        id: `VIEWER.YOU_HAVE_NO_PHOTO_DOCUMENTS`,
                      })
                    )
                  ) : (
                    <>
                      {renderPanelNavigation()}
                      {renderPanelBox()}
                      {showScrollLoader ? (
                        <LoadingData fullmode={true} />
                      ) : undefined}
                    </>
                  )
                ) : (
                  <LoadingData fullmode={true} />
                )}
              </PanelInner>
            </Panel>
          </Content>
        </Wrapper>
        {isProfileCompany && (
          <Filter
            availableFilterAnnotated={true}
            availableFilterVerified={true}
            availableFilterRejected={true}
            availableFilterReserved={true}
            availableFilterTager={true}
          />
        )}
      </Section>
      {visiblePhotoDocumentModal && (
        <PhotoDocumentDetails
          data={selectedPhotoDocument}
          handleClose={() => {
            handleCloseViewPhotoDocumentDetails();
            if (
              !(
                photoDocumentRelabelProcess ||
                photoDocumentRejectProcess ||
                photoDocumentDeleteProcess
              )
            )
              handleAction();
          }}
          handleActionPhotoDocument={(action) => handleAction(action)}
          handleChangePhotoDocument={(type) => handleChangePhotoDocument(type)}
          availableChangeLeftPhotoDocument={availableChangeLeftPhotoDocument}
          availableChangeRightPhotoDocument={availableChangeRightPhotoDocument}
          availableChangeFilter={true}
          availableRelabel={true}
          availableReject={true}
          availableDelete={true}
          availableDownload={true}
        />
      )}
      {visiblePhotoDocumentRelabelModal && (
        <PhotoDocumentRelabel
          id={selectedPhotoDocumentId}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleRelabelProcess(status)}
        />
      )}
      {visiblePhotoDocumentRejectModal && (
        <PhotoDocumentReject
          id={selectedPhotoDocumentId}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleRejectProcess(status)}
        />
      )}
      {visiblePhotoDocumentDeleteModal && (
        <PhotoDocumentDelete
          id={selectedPhotoDocumentId}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleDeleteProcess(status)}
        />
      )}
      {visiblePhotoDocumentsRelabelModal && (
        <PhotoDocumentsRelabel
          ids={selectedPhotoDocuments.map((photoDocument) => photoDocument.id)}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleRelabelProcess(status)}
        />
      )}
      {visiblePhotoDocumentsRejectModal && (
        <PhotoDocumentsReject
          ids={selectedPhotoDocuments.map((photoDocument) => photoDocument.id)}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleRejectProcess(status)}
        />
      )}
      {visiblePhotoDocumentsDeleteModal && (
        <PhotoDocumentsDelete
          ids={selectedPhotoDocuments.map((photoDocument) => photoDocument.id)}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleDeleteProcess(status)}
        />
      )}
    </>
  );
};

export default ViewerPage;
